// pages_annotate.js — Live Object Annotation + Person Classification + Face Recognition Tracking
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { T, Badge, KPI, Panel, Btn, Inp, Modal, PageHeader, AlertBox } from './utils';
const API_BASE = (typeof process!=='undefined'&&process.env?.REACT_APP_API_URL)||'';
const apiFetch = (path,opts={}) => {
  const token = localStorage.getItem('auravex_token');
  return fetch(API_BASE+path,{...opts,headers:{'Content-Type':'application/json','Authorization':token?`Bearer ${token}`:''}})
    .then(r=>r.json()).catch(()=>({}));
};
const apiGet    = p    => apiFetch(p);
const apiPost   = (p,d)=> apiFetch(p,{method:'POST',  body:JSON.stringify(d)});
const apiPut    = (p,d)=> apiFetch(p,{method:'PUT',   body:JSON.stringify(d)});
const apiDelete = p    => apiFetch(p,{method:'DELETE'});

// ── Constants ────────────────────────────────────────────────
const TYPE_META = {
  Employee: { color:'#22c55e', icon:'👔' },
  Customer: { color:'#3b82f6', icon:'🛍' },
  Visitor:  { color:'#f59e0b', icon:'🪪' },
  Suspect:  { color:'#ef4444', icon:'⚠️' },
  Unknown:  { color:'#6b7280', icon:'❓' },
};
const PERSON_TYPES = Object.keys(TYPE_META);
const DEPTS = ['Security','Sales','IT','HR','Operations','Management','Maintenance','Visitor','External'];
const STREAM_URL = 'http://localhost:8080/stream';

// ── Avatar ────────────────────────────────────────────────────
const Avatar = ({ name, image, size=38, type='Unknown' }) => {
  const c = TYPE_META[type]?.color || '#6b7280';
  if (image) return <img src={image} alt="" style={{width:size,height:size,borderRadius:'50%',objectFit:'cover',border:`2px solid ${c}`}}/>;
  const initials = (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
  return (
    <div style={{width:size,height:size,borderRadius:'50%',background:`${c}22`,border:`2px solid ${c}`,
      display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.35,fontWeight:800,color:c}}>
      {initials}
    </div>
  );
};

// ── Annotation Canvas (draws on top of live MJPEG stream) ─────
const AnnotationCanvas = ({ annotations, onBoxDrawn, onBoxClicked }) => {
  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [startPt, setStartPt] = useState(null);
  const [curBox,  setCurBox]  = useState(null);
  const [streamOk,setStreamOk]= useState(false);

  // Redraw every time annotations or curBox changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw saved boxes
    annotations.forEach(ann => {
      const b = typeof ann.bbox === 'string' ? JSON.parse(ann.bbox||'{}') : (ann.bbox||{});
      if (!b.x) return;
      const meta = TYPE_META[ann.person_type] || TYPE_META.Unknown;
      const label = ann.label || ann.name || ann.person_type || 'Unknown';

      // Box fill
      ctx.fillStyle = meta.color + '20';
      ctx.fillRect(b.x, b.y, b.w, b.h);
      // Box border
      ctx.strokeStyle = meta.color;
      ctx.lineWidth = 2;
      ctx.strokeRect(b.x, b.y, b.w, b.h);

      // Corner accents
      const cs = 10;
      ctx.lineWidth = 3;
      [[b.x,b.y],[b.x+b.w-cs,b.y],[b.x,b.y+b.h-cs],[b.x+b.w-cs,b.y+b.h-cs]].forEach(([px,py])=>{
        ctx.beginPath(); ctx.moveTo(px,py+cs); ctx.lineTo(px,py); ctx.lineTo(px+cs,py); ctx.stroke();
      });

      // Label tag
      const tagText = `${meta.icon} ${label}`;
      const tagW = Math.max(b.w, tagText.length * 7 + 10);
      ctx.fillStyle = meta.color;
      ctx.fillRect(b.x, b.y - 22, tagW, 22);
      ctx.fillStyle = '#000';
      ctx.font = 'bold 10px monospace';
      ctx.fillText(tagText, b.x+4, b.y-7);

      // Employee ID badge
      if (ann.employee_id) {
        ctx.fillStyle = '#000000bb';
        ctx.fillRect(b.x, b.y+b.h-18, b.w, 18);
        ctx.fillStyle = meta.color;
        ctx.font = '9px monospace';
        ctx.fillText(`ID:${ann.employee_id}  ${ann.department||''}`, b.x+4, b.y+b.h-5);
      }

      // Tracking dot
      ctx.beginPath();
      ctx.arc(b.x+b.w/2, b.y+b.h+8, 4, 0, Math.PI*2);
      ctx.fillStyle = meta.color;
      ctx.fill();
    });

    // Draw current selection box
    if (curBox) {
      ctx.strokeStyle = '#f5c542';
      ctx.lineWidth = 2;
      ctx.setLineDash([5,3]);
      ctx.strokeRect(curBox.x, curBox.y, curBox.w, curBox.h);
      ctx.setLineDash([]);
      ctx.fillStyle = '#f5c54215';
      ctx.fillRect(curBox.x, curBox.y, curBox.w, curBox.h);
      ctx.fillStyle = '#f5c542';
      ctx.font = '10px monospace';
      ctx.fillText('Draw box around object', curBox.x+4, curBox.y-5);
    }
  }, [annotations, curBox]);

  const getPos = e => {
    const r = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / r.width;
    const scaleY = canvasRef.current.height / r.height;
    return { x: (e.clientX - r.left)*scaleX, y: (e.clientY - r.top)*scaleY };
  };

  const onMouseDown = e => {
    const pos = getPos(e);
    // Check click on existing box
    const hit = annotations.find(ann => {
      const b = typeof ann.bbox === 'string' ? JSON.parse(ann.bbox||'{}') : (ann.bbox||{});
      return b.x && pos.x>=b.x && pos.x<=b.x+b.w && pos.y>=b.y && pos.y<=b.y+b.h;
    });
    if (hit) { onBoxClicked(hit); return; }
    setDrawing(true);
    setStartPt(pos);
  };

  const onMouseMove = e => {
    if (!drawing || !startPt) return;
    const pos = getPos(e);
    setCurBox({ x:Math.min(startPt.x,pos.x), y:Math.min(startPt.y,pos.y),
                w:Math.abs(pos.x-startPt.x),  h:Math.abs(pos.y-startPt.y) });
  };

  const onMouseUp = e => {
    if (!drawing) return;
    setDrawing(false);
    const pos = getPos(e);
    const box = { x:Math.min(startPt.x,pos.x), y:Math.min(startPt.y,pos.y),
                  w:Math.abs(pos.x-startPt.x),  h:Math.abs(pos.y-startPt.y) };
    if (box.w > 15 && box.h > 15) {
      // Crop face from stream image
      let faceImage = null;
      try {
        const img = imgRef.current;
        if (img && img.complete) {
          const c2 = document.createElement('canvas');
          c2.width = box.w; c2.height = box.h;
          c2.getContext('2d').drawImage(img, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
          faceImage = c2.toDataURL('image/jpeg', 0.85);
        }
      } catch(err){}
      onBoxDrawn({ bbox: box, faceImage });
    }
    setCurBox(null);
    setStartPt(null);
  };

  return (
    <div style={{position:'relative', background:'#000', borderRadius:8, overflow:'hidden'}}>
      {/* MJPEG stream */}
      <img ref={imgRef} src={`${STREAM_URL}?t=${Date.now()}`} alt="Live"
        crossOrigin="anonymous"
        onLoad={() => setStreamOk(true)}
        onError={() => setStreamOk(false)}
        style={{width:'100%', display:'block', borderRadius:8}}/>

      {/* Fallback placeholder */}
      {!streamOk && (
        <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',
          alignItems:'center',justifyContent:'center',background:'#0a0e1a',minHeight:360,gap:8}}>
          <div style={{fontSize:36}}>📡</div>
          <div style={{fontSize:12,color:T.text,fontWeight:700}}>Edge Agent Not Running</div>
          <div style={{fontSize:10,color:T.muted}}>Start WEBCAM.bat or IPCAM.bat</div>
          <code style={{fontSize:9,color:T.green,background:'#000',padding:'6px 12px',borderRadius:6}}>
            python edge_agent.py --cam 0 --stream-port 8080
          </code>
        </div>
      )}

      {/* Annotation canvas overlay */}
      <canvas ref={canvasRef} width={640} height={480}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove}
        onMouseUp={onMouseUp} onMouseLeave={()=>{setDrawing(false);setCurBox(null);}}
        style={{position:'absolute',inset:0,width:'100%',height:'100%',cursor:'crosshair',zIndex:10}}/>

      {/* HUD */}
      <div style={{position:'absolute',top:6,left:8,zIndex:20,display:'flex',gap:6,alignItems:'center'}}>
        <span style={{fontSize:8,padding:'2px 7px',borderRadius:8,fontFamily:'monospace',fontWeight:700,
          background: streamOk ? '#22c55e22' : '#f5c54222',
          color: streamOk ? '#22c55e' : '#f5c542',
          border: `1px solid ${streamOk ? '#22c55e44' : '#f5c54244'}`}}>
          {streamOk ? '● LIVE' : '● NO STREAM'}
        </span>
        <span style={{fontSize:8,color:T.muted,background:'rgba(0,0,0,.7)',padding:'2px 6px',borderRadius:4,fontFamily:'monospace'}}>
          {annotations.length} annotations
        </span>
      </div>

      {/* Instructions */}
      <div style={{position:'absolute',bottom:6,left:8,right:8,zIndex:20,
        background:'rgba(0,0,0,.75)',borderRadius:5,padding:'4px 8px',
        fontSize:8,color:T.muted,textAlign:'center',fontFamily:'monospace'}}>
        🖱 DRAW BOX around any object · CLICK existing box to edit · Boxes saved automatically
      </div>
    </div>
  );
};

// ── Person Card ───────────────────────────────────────────────
const PersonCard = ({ person, onEdit, onDelete, onTrack, isTracking }) => {
  const meta = TYPE_META[person.person_type] || TYPE_META.Unknown;
  return (
    <div style={{background:T.deep,borderRadius:9,padding:'10px 12px',border:`1px solid ${T.border}`,
      borderLeft:`3px solid ${meta.color}`,marginBottom:8}}>
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        <Avatar name={person.name} image={person.face_image} size={42} type={person.person_type}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:800,fontSize:12,color:T.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            {person.name || 'Unknown'}
          </div>
          <div style={{fontSize:9,color:T.muted,marginTop:1}}>
            {person.employee_id ? `ID: ${person.employee_id}` : person.id}
            {person.department ? ` · ${person.department}` : ''}
          </div>
          <span style={{fontSize:8,padding:'1px 6px',borderRadius:8,marginTop:3,display:'inline-block',
            background:`${meta.color}18`,color:meta.color,border:`1px solid ${meta.color}30`,fontWeight:700}}>
            {meta.icon} {person.person_type}
          </span>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:4}}>
          <Btn sm onClick={()=>onTrack(person)}
            style={{fontSize:8,padding:'3px 6px',
              background: isTracking ? `${meta.color}22` : 'transparent',
              border:`1px solid ${isTracking ? meta.color : T.border}`,
              color: isTracking ? meta.color : T.muted}}>
            {isTracking ? '🔴 Tracking' : '📍 Track'}
          </Btn>
          <Btn sm v="ghost" onClick={()=>onEdit(person)} style={{fontSize:8,padding:'3px 6px'}}>✏ Edit</Btn>
          <Btn sm v="danger" onClick={()=>onDelete(person)} style={{fontSize:8,padding:'3px 6px'}}>🗑</Btn>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────
export const PageAnnotate = ({ tenants, cameras, toast, logAudit }) => {
  const [persons,     setPersons]    = useState([]);
  const [annotations, setAnnotations]= useState([]);
  const [tracking,    setTracking]   = useState(new Set());
  const [stats,       setStats]      = useState({});
  const [showRegister,setShowRegister]=useState(false);
  const [showEdit,    setShowEdit]   = useState(null);
  const [showDelete,  setShowDelete] = useState(null);
  const [showAnnotate,setShowAnnotate]=useState(null); // {bbox, faceImage}
  const [filterType,  setFilterType] = useState('All');

  // Form state
  const blank = { name:'', person_type:'Unknown', employee_id:'', department:'', face_image:'', access_zones:[], notes:'' };
  const [form, setForm] = useState(blank);
  const ff = k => v => setForm(p=>({...p,[k]:v}));
  const photoRef = useRef(null);

  // Load persons
  const loadPersons = async () => {
    try {
      const data = await apiGet('/api/persons');
      if (Array.isArray(data)) setPersons(data);
      const s = await apiGet('/api/persons/stats');
      if (s) setStats(s);
    } catch(e) {}
  };

  // Load annotations
  const loadAnnotations = async () => {
    try {
      const data = await apiGet('/api/persons/live-annotations');
      if (Array.isArray(data)) setAnnotations(data);
    } catch(e) {}
  };

  useEffect(() => {
    loadPersons();
    loadAnnotations();
    const t = setInterval(() => { loadAnnotations(); }, 5000);
    return () => clearInterval(t);
  }, []);

  // Handle box drawn on canvas
  const onBoxDrawn = (boxData) => {
    setShowAnnotate(boxData);
  };

  // Handle click on existing annotation
  const onBoxClicked = (ann) => {
    const person = persons.find(p => p.id === ann.person_id);
    if (person) setShowEdit(person);
    else setShowAnnotate({ bbox: ann.bbox, faceImage: ann.face_image, existing: ann });
  };

  // Save annotation
  const saveAnnotation = async (annData) => {
    try {
      const r = await apiPost('/api/persons/annotate', {
        cam_id: cameras[0]?.id || 'CAM-001',
        tenant_id: tenants[0]?.id,
        label: annData.name || annData.person_type,
        person_type: annData.person_type,
        bbox: showAnnotate.bbox,
        face_image: showAnnotate.faceImage,
        create_profile: annData.create_profile,
        ...annData,
      });
      if (r?.personId) {
        toast(`✅ Profile created: ${annData.name || annData.person_type}`);
        loadPersons();
      } else {
        toast('✅ Annotation saved');
      }
      loadAnnotations();
    } catch(e) { toast('Error: ' + e.message, 'warn'); }
    setShowAnnotate(null);
  };

  // Register new person
  const registerPerson = async () => {
    if (!form.name.trim()) return toast('Name required', 'warn');
    try {
      const saved = await apiPost('/api/persons', { ...form, tenant_id: tenants[0]?.id });
      if (saved?.error) return toast(saved.error, 'warn');
      setPersons(p => [saved, ...p]);
      setShowRegister(false);
      setForm(blank);
      toast(`✅ ${form.name} registered`);
      logAudit?.('PERSON_REGISTERED', form.name, 'Info');
    } catch(e) { toast(e.message, 'warn'); }
  };

  // Update person
  const updatePerson = async () => {
    try {
      const saved = await apiPut(`/api/persons/${showEdit.id}`, form);
      if (saved?.error) return toast(saved.error, 'warn');
      setPersons(p => p.map(x => x.id === showEdit.id ? saved : x));
      setShowEdit(null);
      toast('✅ Profile updated');
    } catch(e) { toast(e.message, 'warn'); }
  };

  // Delete
  const deletePerson = async () => {
    await apiDelete(`/api/persons/${showDelete.id}`);
    setPersons(p => p.filter(x => x.id !== showDelete.id));
    setShowDelete(null);
    toast('Deleted', 'warn');
  };

  // Toggle tracking
  const toggleTrack = (person) => {
    setTracking(prev => {
      const next = new Set(prev);
      if (next.has(person.id)) next.delete(person.id);
      else next.add(person.id);
      return next;
    });
    toast(`${tracking.has(person.id) ? '⏹ Stopped' : '📍 Tracking'} ${person.name}`);
  };

  // Capture photo from file input
  const capturePhoto = (e, target) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      if (target === 'form') ff('face_image')(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const filteredPersons = filterType === 'All' ? persons
    : persons.filter(p => p.person_type === filterType);

  // PersonForm shared component
  const PersonForm = ({ data, onChange }) => (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      {/* Face photo */}
      <div style={{display:'flex',alignItems:'center',gap:12}}>
        <div onClick={() => photoRef.current?.click()}
          style={{cursor:'pointer',position:'relative'}}>
          <Avatar name={data.name} image={data.face_image} size={72} type={data.person_type}/>
          <div style={{position:'absolute',bottom:0,right:0,background:T.accent,borderRadius:'50%',
            width:20,height:20,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10}}>📷</div>
        </div>
        <input ref={photoRef} type="file" accept="image/*" style={{display:'none'}}
          onChange={e => capturePhoto(e,'form')}/>
        <div style={{flex:1}}>
          <div style={{fontSize:10,color:T.muted,marginBottom:4}}>Click photo to upload face image</div>
          <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
            {PERSON_TYPES.map(t => {
              const m = TYPE_META[t];
              return (
                <button key={t} onClick={() => onChange('person_type')(t)}
                  style={{fontSize:9,padding:'3px 8px',borderRadius:8,cursor:'pointer',
                    background: data.person_type===t ? `${m.color}22` : T.deep,
                    border:`1px solid ${data.person_type===t ? m.color : T.border}`,
                    color: data.person_type===t ? m.color : T.muted, fontWeight:600}}>
                  {m.icon} {t}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
        <Inp label="Full Name *" value={data.name} onChange={onChange('name')} placeholder="John Doe"/>
        <Inp label="Employee / Visitor ID" value={data.employee_id} onChange={onChange('employee_id')} placeholder="EMP-001"/>
      </div>

      <div>
        <div style={{fontSize:10,color:T.muted,fontWeight:700,marginBottom:6}}>DEPARTMENT</div>
        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
          {DEPTS.map(d => (
            <button key={d} onClick={() => onChange('department')(d)}
              style={{fontSize:9,padding:'3px 8px',borderRadius:8,cursor:'pointer',
                background: data.department===d ? `${T.accent}22` : T.deep,
                border:`1px solid ${data.department===d ? T.accent : T.border}`,
                color: data.department===d ? T.accent : T.muted}}>
              {d}
            </button>
          ))}
        </div>
      </div>

      <Inp label="Notes / Access Restrictions" value={data.notes} onChange={onChange('notes')}
        placeholder="Allowed in Zone A, B. Not authorized for server room."/>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <PageHeader
        title="Live Object Annotation & Person Recognition"
        sub="Draw boxes on live feed · Classify persons · Face recognition · Real-time ID tracking">
        <Btn sm v="ghost" onClick={loadPersons}>↻ Refresh</Btn>
        <Btn sm onClick={() => { setForm(blank); setShowRegister(true); }}>+ Register Person</Btn>
      </PageHeader>

      {/* KPIs */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10}}>
        <KPI label="Total Profiles" value={persons.length}           icon="🧑" color={T.blue}/>
        <KPI label="Employees"      value={stats.employees||0}       icon="👔" color={T.green}/>
        <KPI label="Customers"      value={stats.customers||0}       icon="🛍" color={T.blue}/>
        <KPI label="Visitors"       value={stats.visitors||0}        icon="🪪" color={T.accent}/>
        <KPI label="Tracking Now"   value={tracking.size}            icon="📍" color={T.red}/>
      </div>

      {/* How to use */}
      <Panel title="HOW TO USE — LIVE ANNOTATION">
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
          {[
            ['1','🖱','Draw Box','Click and drag on live camera feed to draw a box around any person or object'],
            ['2','🏷','Classify','Label them: Employee, Customer, Visitor, or Suspect'],
            ['3','💾','Register','Save their face image and ID to the Person Registry for future recognition'],
            ['4','📍','Track','Click Track on any registered person to highlight them on the live feed'],
          ].map(([n,icon,title,desc]) => (
            <div key={n} style={{background:T.deep,borderRadius:8,padding:'10px',textAlign:'center',border:`1px solid ${T.border}`}}>
              <div style={{width:24,height:24,borderRadius:'50%',background:T.accent,color:'#000',
                display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:800,margin:'0 auto 6px'}}>{n}</div>
              <div style={{fontSize:16,marginBottom:4}}>{icon}</div>
              <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:3}}>{title}</div>
              <div style={{fontSize:9,color:T.muted,lineHeight:1.5}}>{desc}</div>
            </div>
          ))}
        </div>
      </Panel>

      <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:14}}>

        {/* LEFT — Live feed with canvas */}
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <Panel title="🎥 LIVE FEED — CLICK & DRAG TO ANNOTATE">
            <AnnotationCanvas
              annotations={annotations}
              onBoxDrawn={onBoxDrawn}
              onBoxClicked={onBoxClicked}/>
          </Panel>

          {/* Recent annotations */}
          {annotations.length > 0 && (
            <Panel title={`RECENT ANNOTATIONS (${annotations.length})`}>
              <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:200,overflowY:'auto'}}>
                {annotations.slice(0,10).map((ann,i) => {
                  const meta = TYPE_META[ann.person_type] || TYPE_META.Unknown;
                  return (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 8px',
                      background:T.deep,borderRadius:6,border:`1px solid ${T.border}`}}>
                      <span style={{fontSize:16}}>{meta.icon}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:10,fontWeight:700,color:T.text}}>
                          {ann.label || ann.person_name || ann.person_type}
                        </div>
                        <div style={{fontSize:8,color:T.muted}}>
                          {ann.cam_id} · {new Date(ann.frame_time||Date.now()).toLocaleTimeString()}
                          {ann.employee_id && ` · ID: ${ann.employee_id}`}
                        </div>
                      </div>
                      <span style={{fontSize:8,padding:'2px 6px',borderRadius:8,
                        background:`${meta.color}18`,color:meta.color,fontWeight:700}}>
                        {ann.person_type}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}
        </div>

        {/* RIGHT — Person Registry */}
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {/* Filter tabs */}
          <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
            {['All',...PERSON_TYPES].map(t => {
              const meta = t !== 'All' ? TYPE_META[t] : null;
              const count = t === 'All' ? persons.length : persons.filter(p=>p.person_type===t).length;
              return (
                <button key={t} onClick={() => setFilterType(t)}
                  style={{fontSize:9,padding:'4px 10px',borderRadius:10,cursor:'pointer',
                    background: filterType===t ? (meta ? `${meta.color}22` : `${T.accent}22`) : T.deep,
                    border:`1px solid ${filterType===t ? (meta?.color||T.accent) : T.border}`,
                    color: filterType===t ? (meta?.color||T.accent) : T.muted, fontWeight:600}}>
                  {meta?.icon||'👥'} {t} ({count})
                </button>
              );
            })}
          </div>

          <Panel title={`PERSON REGISTRY (${filteredPersons.length})`}>
            {filteredPersons.length === 0 ? (
              <div style={{textAlign:'center',padding:'28px 0',color:T.muted}}>
                <div style={{fontSize:28,marginBottom:8}}>👤</div>
                <div style={{fontSize:11}}>No profiles yet</div>
                <div style={{fontSize:9,marginTop:4}}>Draw a box on the live feed<br/>or click "+ Register Person"</div>
              </div>
            ) : (
              <div style={{maxHeight:560,overflowY:'auto'}}>
                {filteredPersons.map(p => (
                  <PersonCard key={p.id} person={p}
                    isTracking={tracking.has(p.id)}
                    onEdit={person => { setForm({...person}); setShowEdit(person); }}
                    onDelete={setShowDelete}
                    onTrack={toggleTrack}/>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>

      {/* ── ANNOTATE Modal (when box drawn) ──────────────────── */}
      <Modal open={!!showAnnotate} title="🏷 Classify Detected Object"
        onClose={() => setShowAnnotate(null)}>
        {showAnnotate && <AnnotateForm
          faceImage={showAnnotate.faceImage}
          persons={persons}
          onSave={saveAnnotation}
          onCancel={() => setShowAnnotate(null)}/>}
      </Modal>

      {/* ── REGISTER Modal ────────────────────────────────────── */}
      <Modal open={showRegister} title="👤 Register New Person"
        onClose={() => setShowRegister(false)}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <PersonForm data={form} onChange={ff}/>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <Btn v="ghost" onClick={() => setShowRegister(false)}>Cancel</Btn>
            <Btn onClick={registerPerson}>Register Person</Btn>
          </div>
        </div>
      </Modal>

      {/* ── EDIT Modal ────────────────────────────────────────── */}
      <Modal open={!!showEdit} title={`✏ Edit — ${showEdit?.name}`}
        onClose={() => setShowEdit(null)}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <PersonForm data={form} onChange={ff}/>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <Btn v="ghost" onClick={() => setShowEdit(null)}>Cancel</Btn>
            <Btn onClick={updatePerson}>Save Changes</Btn>
          </div>
        </div>
      </Modal>

      {/* ── DELETE Confirm ────────────────────────────────────── */}
      <Modal open={!!showDelete} title="⚠️ Delete Profile"
        onClose={() => setShowDelete(null)}>
        {showDelete && (
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <AlertBox type="error">
              Delete <b>{showDelete.name}</b>? All tracking history will be lost.
            </AlertBox>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <Btn v="ghost" onClick={() => setShowDelete(null)}>Cancel</Btn>
              <Btn v="danger" onClick={deletePerson}>Delete</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ── Annotate Form (shown after drawing box) ───────────────────
const AnnotateForm = ({ faceImage, persons, onSave, onCancel }) => {
  const [tab,        setTab]      = useState('existing'); // 'existing' | 'new'
  const [personType, setPersonType]=useState('Unknown');
  const [name,       setName]     = useState('');
  const [empId,      setEmpId]    = useState('');
  const [dept,       setDept]     = useState('');
  const [matchId,    setMatchId]  = useState('');
  const [createProf, setCreateProf]=useState(true);

  const handleSave = () => {
    if (tab === 'existing' && matchId) {
      const p = persons.find(x => x.id === matchId);
      onSave({ person_id: matchId, name: p?.name, person_type: p?.person_type,
                employee_id: p?.employee_id, create_profile: false });
    } else {
      onSave({ name, person_type: personType, employee_id: empId, department: dept,
                create_profile: createProf });
    }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      {/* Face crop */}
      {faceImage && (
        <div style={{textAlign:'center'}}>
          <img src={faceImage} alt="Captured" style={{width:120,height:120,borderRadius:8,
            objectFit:'cover',border:`2px solid ${T.accent}`}}/>
          <div style={{fontSize:9,color:T.muted,marginTop:4}}>Captured from live feed</div>
        </div>
      )}

      {/* Tab selector */}
      <div style={{display:'flex',gap:0,border:`1px solid ${T.border}`,borderRadius:8,overflow:'hidden'}}>
        {[['existing','Match Existing Person'],['new','Register as New']].map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)} style={{flex:1,padding:'8px',fontSize:10,
            background: tab===t ? `${T.accent}22` : T.deep, color: tab===t ? T.accent : T.muted,
            border:'none',cursor:'pointer',fontWeight: tab===t ? 700 : 400}}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'existing' ? (
        <div>
          <div style={{fontSize:10,color:T.muted,fontWeight:700,marginBottom:8}}>SELECT PERSON FROM REGISTRY</div>
          {persons.length === 0 ? (
            <div style={{fontSize:10,color:T.muted,textAlign:'center',padding:'12px'}}>
              No persons registered yet. Use "Register as New" tab.
            </div>
          ) : (
            <div style={{maxHeight:200,overflowY:'auto',display:'flex',flexDirection:'column',gap:5}}>
              {persons.map(p => {
                const meta = TYPE_META[p.person_type] || TYPE_META.Unknown;
                return (
                  <div key={p.id} onClick={() => setMatchId(p.id)}
                    style={{display:'flex',alignItems:'center',gap:10,padding:'7px 10px',
                      borderRadius:7,cursor:'pointer',
                      background: matchId===p.id ? `${meta.color}18` : T.deep,
                      border:`1px solid ${matchId===p.id ? meta.color : T.border}`}}>
                    <Avatar name={p.name} image={p.face_image} size={32} type={p.person_type}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:11,fontWeight:700,color:T.text}}>{p.name}</div>
                      <div style={{fontSize:9,color:T.muted}}>
                        {meta.icon} {p.person_type}{p.employee_id ? ` · ID: ${p.employee_id}` : ''}
                        {p.department ? ` · ${p.department}` : ''}
                      </div>
                    </div>
                    {matchId===p.id && <span style={{color:meta.color,fontWeight:800}}>✓</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <div>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,marginBottom:6}}>PERSON TYPE</div>
            <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
              {PERSON_TYPES.map(t => {
                const m = TYPE_META[t];
                return (
                  <button key={t} onClick={() => setPersonType(t)}
                    style={{fontSize:9,padding:'4px 10px',borderRadius:8,cursor:'pointer',
                      background: personType===t ? `${m.color}22` : T.deep,
                      border:`1px solid ${personType===t ? m.color : T.border}`,
                      color: personType===t ? m.color : T.muted, fontWeight:600}}>
                    {m.icon} {t}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <Inp label="Name" value={name} onChange={setName} placeholder="Full name"/>
            <Inp label="Employee ID" value={empId} onChange={setEmpId} placeholder="EMP-001"/>
          </div>
          <div>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,marginBottom:5}}>DEPARTMENT</div>
            <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
              {DEPTS.slice(0,5).map(d => (
                <button key={d} onClick={() => setDept(d)}
                  style={{fontSize:9,padding:'3px 7px',borderRadius:6,cursor:'pointer',
                    background: dept===d ? `${T.accent}22` : T.deep,
                    border:`1px solid ${dept===d ? T.accent : T.border}`,
                    color: dept===d ? T.accent : T.muted}}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <input type="checkbox" checked={createProf} onChange={e=>setCreateProf(e.target.checked)}/>
            <span style={{fontSize:10,color:T.muted}}>Save to Person Registry for future recognition</span>
          </div>
        </div>
      )}

      <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:4}}>
        <Btn v="ghost" onClick={onCancel}>Cancel</Btn>
        <Btn onClick={handleSave}>
          {tab === 'existing' ? '✅ Confirm Match' : '💾 Save & Annotate'}
        </Btn>
      </div>
    </div>
  );
};

export default PageAnnotate;
